import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Plus, Trash2, Coffee, UtensilsCrossed, Moon, Cookie } from "lucide-react";

const MEAL_META = {
  breakfast: { label: "Breakfast", icon: Coffee, color: "amber", ring: "ring-amber-200", bg: "from-amber-50 to-orange-50" },
  lunch: { label: "Lunch", icon: UtensilsCrossed, color: "emerald", ring: "ring-emerald-200", bg: "from-emerald-50 to-teal-50" },
  dinner: { label: "Dinner", icon: Moon, color: "indigo", ring: "ring-indigo-200", bg: "from-indigo-50 to-purple-50" },
  snacks: { label: "Snacks", icon: Cookie, color: "pink", ring: "ring-pink-200", bg: "from-pink-50 to-rose-50" },
};

export default function MealSection({ meal, data, onAdd, onDelete }) {
  const meta = MEAL_META[meal];
  const Icon = meta.icon;
  const entries = data?.entries || [];
  return (
    <Card className={`border-0 shadow-md bg-gradient-to-br ${meta.bg}`} data-testid={`meal-section-${meal}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg bg-white/70 text-${meta.color}-600`}>
              <Icon className="w-4 h-4" />
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white text-base">{meta.label}</h3>
            <span className="text-xs tabular-nums text-gray-600 font-semibold">
              {Math.round(data?.calories || 0)} kcal
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => onAdd(meal)}
            className={`h-8 px-2.5 bg-white/80 hover:bg-white text-${meta.color}-700 border-0 shadow-sm`}
            data-testid={`add-food-${meal}`}
          >
            <Plus className="w-4 h-4 mr-1" /> Add food
          </Button>
        </div>

        {entries.length === 0 ? (
          <p className="text-xs text-gray-500 italic py-2 text-center">No items yet.</p>
        ) : (
          <div className="space-y-1.5">
            {entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 bg-white/70 rounded-lg p-2 group">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {e.food_name}
                    {e.brand && <span className="text-xs text-gray-500 font-normal"> · {e.brand}</span>}
                  </div>
                  <div className="text-[11px] text-gray-500 tabular-nums">
                    {e.servings}× {e.serving_description || `${e.serving_size}${e.serving_unit || 'g'}`}
                    <span className="mx-1 text-gray-300">·</span>
                    P {Math.round(e.protein_g)}g · C {Math.round(e.carbs_g)}g · F {Math.round(e.fats_g)}g
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-bold text-orange-600 tabular-nums">{Math.round(e.calories)}</span>
                  <button
                    onClick={() => onDelete(e.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                    title="Remove"
                    data-testid={`delete-entry-${e.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {entries.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-200/70 text-[11px] text-gray-600 tabular-nums flex justify-end gap-3">
            <span>P {Math.round(data.protein_g || 0)}g</span>
            <span>C {Math.round(data.carbs_g || 0)}g</span>
            <span>F {Math.round(data.fats_g || 0)}g</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
